import { useEffect, useMemo, useState } from 'react';
import { Link, NavLink, Outlet, matchPath, useLocation } from 'react-router-dom';
import BrandLogo from './BrandLogo';
import CompanySwitcher from './CompanySwitcher';
import { IconChevronDown, IconChevronRight, IconMenu, IconSidebar, IconSignOut } from './NavIcons';
import { useAppDispatch, useAppSelector } from '../hooks/redux';
import { logout } from '../features/auth/authSlice';
import { useCompanies } from '../features/company/CompanyContext';
import api from '../services/api';
import JourneyTabs from './JourneyTabs';
import DemoBanner from './DemoBanner';
import { JOURNEY } from '../lib/format';
import { COMPANY_ROUTES, HOME_PATH, NAV_SECTIONS, type NavBadgeKey, type NavItem, type NavSection } from '../app/navigation';

const COLLAPSE_KEY = 'micus_musb_sidebar_collapsed';
const SECTIONS_KEY = 'micus_musb_nav_sections';

type Badges = Partial<Record<NavBadgeKey, number>>;

function loadClosedSections(): Record<string, boolean> {
  try {
    return JSON.parse(localStorage.getItem(SECTIONS_KEY) || '{}');
  } catch {
    return {};
  }
}

type Journey = { status: string | null; ratingLocked: boolean };

/** One overview fetch per navigation feeds the menu badges and the journey tabs. */
function useNavState(pathname: string, companyVersion: number, companyCount: number): { badges: Badges; journey: Journey } {
  const [badges, setBadges] = useState<Badges>({});
  const [journey, setJourney] = useState<Journey>({ status: null, ratingLocked: false });
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const { data } = await api.get('/customer/overview');
        if (cancelled) return;
        if (!data.company) {
          setBadges({});
          setJourney({ status: null, ratingLocked: false });
          return;
        }
        const docs: { status: string }[] = data.documents || [];
        setBadges({
          docIssues: docs.filter((d) => d.status === 'NEEDS_REVIEW' || d.status === 'REJECTED').length,
          pendingPayments: data.pendingPayments || 0,
        });
        setJourney({ status: data.company.status, ratingLocked: Boolean(data.ratingLocked) });
      } catch {
        /* badges are decorative */
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [pathname, companyVersion]);
  return { badges: { ...badges, companies: companyCount > 1 ? companyCount : 0 }, journey };
}

export default function WorkspaceShell() {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((s) => s.auth);
  const { version, companies, active } = useCompanies();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(COLLAPSE_KEY) === '1');
  const [closedSections, setClosedSections] = useState<Record<string, boolean>>(loadClosedSections);
  const { badges, journey } = useNavState(location.pathname, version, companies.length);
  const sections = useMemo(() => NAV_SECTIONS.filter((s) => !s.requiresCompany || companies.length > 0), [companies.length]);
  const onCompanyRoute = companies.length > 0 && COMPANY_ROUTES.some((to) => matchPath({ path: to, end: true }, location.pathname));

  useEffect(() => {
    localStorage.setItem(COLLAPSE_KEY, collapsed ? '1' : '0');
  }, [collapsed]);

  useEffect(() => {
    localStorage.setItem(SECTIONS_KEY, JSON.stringify(closedSections));
  }, [closedSections]);

  useEffect(() => setMobileOpen(false), [location.pathname]);

  const current = useMemo(() => {
    for (const section of NAV_SECTIONS) {
      for (const item of section.items) {
        if (matchPath({ path: item.to, end: Boolean(item.end) }, location.pathname)) return { section: section.title, label: item.label };
      }
    }
    const step = JOURNEY.findIndex((s) => matchPath({ path: s.to, end: true }, location.pathname));
    return step >= 0 ? { section: 'Journey', label: `${step + 1} · ${JOURNEY[step].label}` } : null;
  }, [location.pathname]);

  const toggleSection = (id: string) => setClosedSections((s) => ({ ...s, [id]: !s[id] }));

  const renderItem = (item: NavItem, compact: boolean) => {
    const Icon = item.icon;
    const count = item.badge ? badges[item.badge] || 0 : 0;
    return (
      <NavLink
        key={item.to}
        to={item.to}
        end={item.end}
        title={compact ? `${item.label}${count ? ` (${count})` : ''}` : undefined}
        className={({ isActive }) =>
          `group relative flex items-center rounded-xl transition-all ${compact ? 'justify-center px-2 py-2.5' : 'gap-3 px-3 py-2'} ${
            isActive ? 'bg-white text-[#d6006e] shadow-[0_8px_24px_rgba(0,0,0,0.18)]' : 'text-white/85 hover:bg-white/10 hover:text-white'
          }`
        }
      >
        {({ isActive }) => (
          <>
            <Icon className={`h-5 w-5 shrink-0 ${isActive ? 'text-[#d6006e]' : ''}`} />
            {compact ? (
              count > 0 && <span className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-[#ffd166] ring-2 ring-[#7a0038]" aria-hidden />
            ) : (
              <span className="flex min-w-0 flex-1 items-center justify-between gap-2">
                <span className="truncate text-sm font-semibold">{item.label}</span>
                {count > 0 ? (
                  <span
                    className={`min-w-[20px] rounded-full px-1.5 py-0.5 text-center text-[10px] font-black ${
                      isActive ? 'bg-[#d6006e] text-white' : 'bg-white/90 text-[#b0005a]'
                    }`}
                  >
                    {count}
                  </span>
                ) : (
                  <span className={`text-[9px] font-bold uppercase tracking-wider ${isActive ? 'text-[#d6006e]/70' : 'text-white/35'}`}>{item.hint}</span>
                )}
              </span>
            )}
          </>
        )}
      </NavLink>
    );
  };

  const renderSection = (section: NavSection, compact: boolean) => {
    const closed = !compact && closedSections[section.id];
    return (
      <div key={section.id} className="mb-2">
        {compact ? (
          <div className="mx-3 my-2 h-px bg-white/15" aria-hidden />
        ) : (
          <button
            type="button"
            onClick={() => toggleSection(section.id)}
            className="mb-1 flex w-full items-center justify-between rounded-lg px-2 py-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-white/55 hover:text-white/85"
            aria-expanded={!closed}
          >
            <span className="flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full" style={{ background: section.dot }} />
              {section.title}
            </span>
            <IconChevronDown className={`h-3.5 w-3.5 transition-transform ${closed ? '-rotate-90' : ''}`} />
          </button>
        )}
        {!closed && <div className="flex flex-col gap-0.5">{section.items.map((item) => renderItem(item, compact))}</div>}
      </div>
    );
  };

  const sidebar = (compact: boolean, mobile = false) => (
    <aside
      className={`relative flex h-full flex-col text-white transition-[width] duration-200 ${compact ? 'w-[76px]' : 'w-[264px]'}`}
      style={{ background: 'linear-gradient(195deg, #1a000f 0%, #5e002f 38%, #c4005e 74%, #e8007a 100%)' }}
    >
      <div className={`flex items-center gap-2 border-b border-white/10 px-3 py-3 ${compact ? 'justify-center' : 'justify-between'}`}>
        {!compact && <p className="px-2 text-[10px] font-bold uppercase tracking-[0.2em] text-white/60">Menu</p>}
        {mobile ? (
          <button type="button" onClick={() => setMobileOpen(false)} className="rounded-lg p-1.5 text-white/80 hover:bg-white/10" aria-label="Close menu">
            <IconChevronRight />
          </button>
        ) : (
          <button
            type="button"
            onClick={() => setCollapsed((v) => !v)}
            className="rounded-lg p-1.5 text-white/75 transition-colors hover:bg-white/10 hover:text-white"
            aria-label={collapsed ? 'Expand menu' : 'Collapse menu'}
            aria-pressed={collapsed}
            title={collapsed ? 'Expand menu' : 'Collapse menu'}
          >
            <IconSidebar className={`h-5 w-5 transition-transform ${collapsed ? 'scale-x-[-1]' : ''}`} />
          </button>
        )}
      </div>

      <nav className="mz-scroll-thin flex flex-1 flex-col overflow-y-auto px-2 py-3" aria-label="Workspace menu">
        {sections.map((s) => renderSection(s, compact))}
      </nav>
    </aside>
  );

  const userInitial = (user?.name || user?.email || '?').slice(0, 1).toUpperCase();

  return (
    <div className="flex min-h-screen flex-row-reverse bg-[#faf7f9] text-[#1a0010]">
      <div className="sticky top-0 z-40 hidden h-screen shrink-0 lg:block">{sidebar(collapsed)}</div>

      {mobileOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <button type="button" aria-label="Close menu" className="absolute inset-0 bg-black/40" onClick={() => setMobileOpen(false)} />
          <div className="mz-slide-in-right absolute inset-y-0 right-0 shadow-2xl">{sidebar(false, true)}</div>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex items-center justify-between gap-3 border-b border-[#fce7f3] bg-white/85 px-5 py-3 backdrop-blur-md sm:px-8">
          <div className="flex min-w-0 items-center gap-3">
            <Link to={HOME_PATH} className="flex shrink-0 items-center gap-2.5" title="Micus — Overview">
              <BrandLogo size={30} className="shrink-0" />
              <span className="hidden leading-tight md:block">
                <span className="block text-base font-black tracking-tight text-[#6b0030]">Micus</span>
                <span className="block text-[9px] font-bold uppercase tracking-[0.18em] text-[#c084a0]">Customer workspace</span>
              </span>
            </Link>
            <span className="hidden h-8 w-px shrink-0 bg-[#fce7f3] sm:block" aria-hidden />
            <div className="hidden min-w-0 sm:block">
              <p className="truncate text-[10px] font-bold uppercase tracking-[0.2em] text-[#c084a0]">{current?.section || 'Workspace'}</p>
              <p className="truncate text-sm font-black text-[#6b0030]">{current?.label || 'Micus'}</p>
            </div>
          </div>
          <div className="flex min-w-0 items-center gap-2">
            <CompanySwitcher />
            <div className="flex shrink-0 items-center gap-1.5 border-l border-[#fce7f3] pl-2">
              <Link to="/account" className="flex min-w-0 items-center gap-2 rounded-xl p-1 hover:bg-[#fdf2f8]" title="Account settings">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#d6006e] text-xs font-black text-white">{userInitial}</span>
                <span className="hidden min-w-0 max-w-[180px] leading-tight lg:block">
                  <span className="block truncate text-sm font-semibold text-[#1a0010]">{user?.name || user?.email}</span>
                  <span className="block truncate text-[11px] text-[#b0708f]">{user?.email}</span>
                </span>
              </Link>
              <button
                type="button"
                onClick={() => dispatch(logout())}
                className="inline-flex items-center gap-1.5 rounded-xl px-2.5 py-2 text-xs font-bold text-[#d6006e] hover:bg-[#fdf2f8]"
                style={{ border: '1px solid #fce7f3' }}
                title="Sign out"
                aria-label="Sign out"
              >
                <IconSignOut className="h-4 w-4" />
                <span className="hidden xl:inline">Sign out</span>
              </button>
            </div>
            <button
              type="button"
              onClick={() => setMobileOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-xl px-3 py-2 text-xs font-bold text-[#d6006e] lg:hidden"
              style={{ background: '#fdf2f8', border: '1px solid #fce7f3' }}
            >
              <IconMenu className="h-4 w-4" /> Menu
            </button>
          </div>
        </header>

        <main className="flex-1">
          <div key={version} className="mx-auto w-full max-w-6xl px-5 py-6 sm:px-8 sm:py-7">
            {active?.isDemo && <DemoBanner />}
            {onCompanyRoute && (
              <div className="mb-5">
                <JourneyTabs status={journey.status} ratingLocked={journey.ratingLocked} />
              </div>
            )}
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
